import { NewOfferAction } from '../actions';
import {
  SET_OFFER_DAYS_OF_WEEK,
  SET_OFFER_ITEMS,
  SET_OFFER_CATEGORIES,
  SET_OFFER_TYPE,
  SET_OFFER_DISCOUNT,
  SET_OFFER_FULFILLMENT_METHODS,
  SET_OFFER_END_DATE,
  SET_OFFER_END_TIME,
  SET_OFFER_GOAL,
  SET_OFFER_MOV,
  SET_OFFER_ORG_ID,
  SET_OFFER_SCHEDULE_TYPE,
  SET_OFFER_SELECTED_RESTAURANTS_DRN,
  SET_OFFER_START_DATE,
  SET_OFFER_START_TIME,
  SET_OFFER_THEME,
  SET_OFFER_TIME_OF_DAY_FROM,
  SET_OFFER_TIME_OF_DAY_UNTIL,
  SET_OFFER_TIMEZONE,
  SET_OFFER_UPLOADED_ITEMS,
  CLEAR_NEW_OFFER,
  CLEAR_OFFER_ITEMS,
  VALIDATE_OFFER,
  SET_OFFER_MAX_DISCOUNT,
} from '../actionTypes';
import { NewOfferMenuCategory, OffersState, WeekdayKey } from '../types';

type NewOfferState = OffersState['newOffer'];

export const initialState: NewOfferState = undefined;

function newOfferReducer(
  state: NewOfferState = initialState,
  action: NewOfferAction,
): NewOfferState {
  switch (action.type) {
    case SET_OFFER_DAYS_OF_WEEK: {
      const value = action.value && (Number(action.value) as WeekdayKey);
      const currentDays = new Set(state?.daysOfWeek || []);

      if (value) {
        if (currentDays.has(value)) {
          currentDays.delete(value);
        } else {
          currentDays.add(value);
        }
      }

      return {
        ...state,
        daysOfWeek: value && currentDays.size > 0 ? [...currentDays] : null,
      };
    }

    case SET_OFFER_ITEMS: {
      const { value } = action;
      const currentItems = new Set(state?.menuItems);

      if (currentItems.has(value)) {
        currentItems.delete(value);
      } else {
        currentItems.add(value);
      }

      return {
        ...state,
        menuItems: [...currentItems],
      };
    }

    case SET_OFFER_CATEGORIES: {
      const { value } = action;
      const currentItems = new Set(state?.menuItems);
      const currentCategories = new Map<string, NewOfferMenuCategory>();
      state?.menuCategories?.forEach((v) => currentCategories.set(v.name, v));

      if (currentCategories.has(value.name)) {
        const category = currentCategories.get(
          value.name,
        ) as NewOfferMenuCategory;

        category.itemPks.forEach((itemPk) => currentItems.delete(itemPk));
        currentCategories.delete(value.name);
      } else {
        currentCategories.set(value.name, value);
        value.itemPks.forEach((itemPk) => currentItems.add(itemPk));
      }

      return {
        ...state,
        menuCategories: [...currentCategories.values()],
        menuItems: [...currentItems],
      };
    }

    case SET_OFFER_TYPE: {
      return {
        ...state,
        type: action.value,
      };
    }

    case SET_OFFER_DISCOUNT: {
      return {
        ...state,
        discount: action.value,
      };
    }

    case SET_OFFER_END_DATE: {
      return {
        ...state,
        endAt: action.value,
      };
    }

    case SET_OFFER_END_TIME: {
      return {
        ...state,
        endTime: action.value,
      };
    }

    case SET_OFFER_GOAL: {
      return {
        ...state,
        goal: action.value,
      };
    }

    case SET_OFFER_MOV: {
      return {
        ...state,
        mov: action.value,
      };
    }

    case SET_OFFER_ORG_ID: {
      return {
        ...state,
        orgId: action.value,
      };
    }

    case SET_OFFER_SCHEDULE_TYPE: {
      return {
        ...state,
        scheduleType: action.value,
      };
    }

    case SET_OFFER_SELECTED_RESTAURANTS_DRN: {
      return {
        ...state,
        restaurantDrnIds: action.value,
      };
    }

    case SET_OFFER_START_DATE: {
      return {
        ...state,
        startAt: action.value,
      };
    }

    case SET_OFFER_START_TIME: {
      return {
        ...state,
        startTime: action.value,
      };
    }

    case SET_OFFER_THEME: {
      return {
        ...state,
        theme: action.value,
      };
    }

    case SET_OFFER_TIME_OF_DAY_FROM: {
      return {
        ...state,
        timeOfDayFrom: action.value,
      };
    }

    case SET_OFFER_TIME_OF_DAY_UNTIL: {
      return {
        ...state,
        timeOfDayUntil: action.value,
      };
    }

    case SET_OFFER_TIMEZONE: {
      return {
        ...state,
        timezone: action.value,
      };
    }

    case SET_OFFER_UPLOADED_ITEMS: {
      return {
        ...state,
        uploadedItems: action.uploadedItems,
      };
    }

    case SET_OFFER_FULFILLMENT_METHODS: {
      return {
        ...state,
        fulfillmentMethods: action.value,
      };
    }

    case CLEAR_NEW_OFFER: {
      return initialState;
    }

    case CLEAR_OFFER_ITEMS: {
      return {
        ...state,
        menuItems: initialState?.menuItems,
        menuCategories: initialState?.menuCategories,
      };
    }

    case VALIDATE_OFFER: {
      const { shouldValidate } = action;

      return {
        ...state,
        validate: shouldValidate,
      };
    }

    case SET_OFFER_MAX_DISCOUNT: {
      return {
        ...state,
        maxDiscount: action.value,
      };
    }

    default:
      return state;
  }
}

export default (
  { newOffer, ...state }: OffersState,
  action: NewOfferAction,
): OffersState => ({
  ...state,
  newOffer: newOfferReducer(newOffer, action),
});
